import moment from 'moment';
import {
    DimensionConfig,
    MetricsConfig,
    ChartColorConfig,
    METRIC_API_TYPE,
    COMPARE_PERIODS,
    DIMENSION_SEPARATOR,
    UNKNOWN_DIMENSION,
    NA_VALUE,
    FULL_CHANGE_PCT,
    TIME_BREAKDOWN_ALL,
} from './consts';
import { buildFilter, buildMetricParams } from '../utils/reportQueryUtils';
import { IN_OPERATOR } from '../utils/reportsConsts';

const periodKey = (key, period) => `${key}__${period}`;

// Read raw comparison results: the shared expandRow() explodes calculated-cohort ratio objects
// (its compare metadata key order differs from the result key order), breaking Ad ROI/ROI charts.
export const getReportRows = reportData => reportData?.results || [];

export const getMetricColumnKey = metricName => {
    const config = MetricsConfig[metricName];
    if (!config) return null;

    const { apiName, apiType, period } = config;
    return apiType === METRIC_API_TYPE.COHORT ? periodKey(apiName, period) : apiName;
};

export const getMetricRcaFields = metricName => {
    const config = MetricsConfig[metricName];
    if (!config) return { api_name: '', cohort_period: null };

    const { apiName, apiType, period } = config;
    return {
        api_name: apiName,
        cohort_period: apiType === METRIC_API_TYPE.COHORT ? period : null,
    };
};

export const getCohortWindowDays = metricName => {
    const config = MetricsConfig[metricName];
    if (!config || config.apiType !== METRIC_API_TYPE.COHORT) return null;
    return parseInt(config.period, 10);
};

export const isRecentDataImmature = (metricName, periodEnd) => {
    const windowDays = getCohortWindowDays(metricName);
    if (!windowDays || !periodEnd) return false;
    return moment(periodEnd).isAfter(moment().subtract(windowDays, 'days'), 'day');
};

const resolveValue = (row, key) => {
    const val = row[key];
    if (val == null || val === NA_VALUE) return null;
    if (typeof val === 'number') return Number.isNaN(val) ? null : val;
    if (typeof val === 'object' && val.fields) {
        const [, numerator] = val.fields[0];
        const [, denominator] = val.fields[1];
        return denominator === 0 ? null : numerator / denominator;
    }
    return null;
};

const splitBySign = values => {
    const negatives = [];
    const positives = [];
    values.forEach((val, idx) => {
        if (val == null) return;
        if (val < 0) negatives.push({ val, idx });
        else if (val > 0) positives.push({ val, idx });
    });
    negatives.sort((a, b) => a.val - b.val);
    positives.sort((a, b) => a.val - b.val);
    return { negatives, positives };
};

const assignRanks = (entries, toNormalized) => {
    let rank = 0;
    entries.forEach((entry, i) => {
        if (i > 0 && entry.val !== entries[i - 1].val) rank = i;
        toNormalized(entry.idx, rank);
    });
};

const rankNormalize = values => {
    const normalized = values.map(val => (val == null ? null : 0));
    const { negatives, positives } = splitBySign(values);

    assignRanks(negatives, (idx, rank) => {
        normalized[idx] = -(negatives.length - rank) / negatives.length;
    });
    assignRanks(positives, (idx, rank) => {
        normalized[idx] = (rank + 1) / positives.length;
    });

    return normalized;
};

const hexToRgb = hex => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];

const rgbToHex = ([r, g, b]) =>
    `#${((1 << 24) + (r << 16) + (g << 8) + b)
        .toString(16)
        .slice(1)
        .toLowerCase()}`;

const interpolateColor = (from, to, ratio) => rgbToHex(from.map((c, i) => Math.round(c + (to[i] - c) * ratio)));

const GOOD_RGB = hexToRgb(ChartColorConfig.GOOD);
const BAD_RGB = hexToRgb(ChartColorConfig.BAD);
const NEUTRAL_RGB = hexToRgb(ChartColorConfig.NEUTRAL);
const MIN_COLOR_SATURATION = 0.3;

export const getPointColor = (normalized, increasesAreGood) => {
    if (normalized == null || !Number.isFinite(normalized)) return ChartColorConfig.NO_DATA;
    if (normalized === 0) return ChartColorConfig.NEUTRAL;

    const positiveRgb = increasesAreGood ? GOOD_RGB : BAD_RGB;
    const negativeRgb = increasesAreGood ? BAD_RGB : GOOD_RGB;
    const saturation = MIN_COLOR_SATURATION + (1 - MIN_COLOR_SATURATION) * Math.abs(normalized);

    if (normalized > 0) return interpolateColor(NEUTRAL_RGB, positiveRgb, saturation);
    return interpolateColor(NEUTRAL_RGB, negativeRgb, saturation);
};

const computeChange = (row, p1Key, p2Key) => {
    const p1 = resolveValue(row, p1Key);
    const p2 = resolveValue(row, p2Key);
    if (p1 == null || p2 == null) return { p1, p2, abs: null, pct: null };
    const abs = p1 - p2;
    const pct = p2 !== 0 ? ((p1 - p2) / Math.abs(p2)) * FULL_CHANGE_PCT : p1 !== 0 ? FULL_CHANGE_PCT : 0;
    return { p1, p2, abs, pct };
};

// The id/parent strings are opaque tree keys for Highcharts. Never split them back into
// dimension values — a value may contain DIMENSION_SEPARATOR (e.g. app "Warhammer 40,000: Tacticus").
// The raw values are carried separately on custom.dimValues for any per-dimension logic.
const buildPointId = (row, dimensionKeys) => {
    const dimValues = dimensionKeys.map(k => (row[k] != null ? String(row[k]) : UNKNOWN_DIMENSION));
    return {
        id: dimValues.join(DIMENSION_SEPARATOR),
        parent: dimValues.slice(0, -1).join(DIMENSION_SEPARATOR),
        name: dimValues[dimValues.length - 1],
        dimValues,
    };
};

export const buildRingPoints = (rows, dimensionKeys, sizeKey, colorKey, ringLevel) => {
    const sizeP1Key = periodKey(sizeKey, COMPARE_PERIODS.CURRENT);
    const sizeP2Key = periodKey(sizeKey, COMPARE_PERIODS.PREVIOUS);
    const colorP1Key = periodKey(colorKey, COMPARE_PERIODS.CURRENT);
    const colorP2Key = periodKey(colorKey, COMPARE_PERIODS.PREVIOUS);

    const colorChanges = rows.map(row => computeChange(row, colorP1Key, colorP2Key));
    const sizeChanges = rows.map(row => computeChange(row, sizeP1Key, sizeP2Key));

    return rows
        .map((row, i) => {
            const { dimValues, ...pointId } = buildPointId(row, dimensionKeys);
            const sizeP1Value = sizeChanges[i].p1 ?? 0;
            return {
                ...pointId,
                value: sizeP1Value,
                custom: {
                    dimValues,
                    colorP1: colorChanges[i].p1,
                    colorP2: colorChanges[i].p2,
                    rawColorChange: colorChanges[i].abs,
                    rawColorChangePct: colorChanges[i].pct,
                    sizeP1: sizeChanges[i].p1,
                    sizeP2: sizeChanges[i].p2,
                    rawSizeChange: sizeChanges[i].abs,
                    rawSizeChangePct: sizeChanges[i].pct,
                    ringLevel,
                },
            };
        })
        .filter(point => point.value > 0);
};

export const assignGlobalColors = (points, increasesAreGood) => {
    const pcts = points.map(p => p.custom?.rawColorChangePct ?? null);
    const normalized = rankNormalize(pcts);
    return points.map((point, i) => {
        const color = normalized[i] == null ? ChartColorConfig.NO_DATA : getPointColor(normalized[i], increasesAreGood);
        return {
            ...point,
            color,
            custom: { ...point.custom, originalColor: color },
        };
    });
};

const isAncestorChain = (a, b) => {
    const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
    return shorter.every((val, i) => longer[i] === val);
};

export const getTopMovers = (points, { limit = 10, minSizeShare = 0.01 } = {}) => {
    const totalSize = points.filter(p => p.custom?.ringLevel === 0).reduce((sum, p) => sum + p.value, 0);
    const minSize = totalSize * minSizeShare;

    const candidates = points
        .filter(p => Number.isFinite(p.custom?.rawColorChangePct) && p.custom.rawColorChangePct !== 0)
        .filter(p => p.value >= minSize)
        .sort((a, b) => Math.abs(b.custom.rawColorChangePct) - Math.abs(a.custom.rawColorChangePct));

    const movers = [];
    candidates.forEach(candidate => {
        if (movers.length >= limit) return;
        const overlaps = movers.some(picked => isAncestorChain(picked.custom.dimValues, candidate.custom.dimValues));
        if (!overlaps) movers.push(candidate);
    });
    return movers;
};

export const computeSubtreeColors = (chart, rootId, increasesAreGood) => {
    const { points } = chart.series[0];
    const subtreePrefix = `${rootId}${DIMENSION_SEPARATOR}`;
    const subtreePoints = points.filter(p => p.id === rootId || p.id.startsWith(subtreePrefix));

    const pcts = subtreePoints.map(p => p.custom?.rawColorChangePct ?? null);
    const normalized = rankNormalize(pcts);

    return subtreePoints.map((point, i) => ({
        point,
        color: normalized[i] == null ? ChartColorConfig.NO_DATA : getPointColor(normalized[i], increasesAreGood),
    }));
};

export const restoreOriginalColors = chart =>
    chart.series[0].points.map(point => ({ point, color: point.custom?.originalColor }));

// Request only the selected metrics; sending org-gated cohort metrics (e.g. ad_monetization_roi)
// the org lacks makes the backend reject the whole query with APIRequestedAnInvalidMetric.
const getSelectedMetricConfigs = (colorMetric, sizeMetric) =>
    [colorMetric?.value, sizeMetric?.value]
        .filter(Boolean)
        .map(key => MetricsConfig[key])
        .filter(Boolean);

export const buildRingQueryParams = ({ dimensions, filters, firstPeriod, secondPeriod, colorMetric, sizeMetric }) => {
    const apiFilters = filters
        .filter(f => f.values?.length)
        .map(filter =>
            buildFilter(
                DimensionConfig[filter.type].apiName,
                filter.values.map(value => value.value)
            )
        );

    const baseParams = {
        ...buildMetricParams(getSelectedMetricConfigs(colorMetric, sizeMetric)),
        filters: apiFilters,
        compare: true,
        start_date: firstPeriod.start,
        end_date: firstPeriod.end,
        start_date_2: secondPeriod.start,
        end_date_2: secondPeriod.end,
        time_breakdown: [TIME_BREAKDOWN_ALL],
    };

    return dimensions.map((_, index) => ({
        ...baseParams,
        dimensions: dimensions.slice(0, index + 1).map(d => DimensionConfig[d.value].apiName),
    }));
};

export const buildQueryKey = ({ dimensions, filters, firstPeriod, secondPeriod, colorMetric, sizeMetric }) =>
    JSON.stringify({
        dimensions: dimensions.map(d => d.value),
        filters: filters
            .map(f => ({ type: f.type, values: (f.values || []).map(v => v.value).sort() }))
            .sort((a, b) => a.type.localeCompare(b.type)),
        firstPeriod,
        secondPeriod,
        // metric-specific: the report now fetches only the selected metrics
        colorMetric: colorMetric?.value ?? null,
        sizeMetric: sizeMetric?.value ?? null,
    });

// Map each selected segment value onto its dimension positionally. dimValues come straight
// from the row (see buildPointId), so a value containing DIMENSION_SEPARATOR stays intact.
const buildSegmentFilters = (dimValues, dimensions) =>
    dimensions
        .map((dim, i) => {
            if (i >= dimValues.length) return null;
            const config = DimensionConfig[dim.value || dim];
            if (!config) return null;
            return { dimension: config.apiName, operator: IN_OPERATOR, values: [dimValues[i]] };
        })
        .filter(Boolean);

const normalizeSidebarFilters = filters =>
    (filters || [])
        .filter(f => f.values?.length)
        .map(f => ({
            dimension: DimensionConfig[f.type]?.apiName,
            operator: IN_OPERATOR,
            values: f.values.map(v => v.value),
        }));

const mergeRcaFilters = (segmentFilters, sidebarFilters) => {
    const segmentDimensions = new Set(segmentFilters.map(f => f.dimension));
    return [...segmentFilters, ...sidebarFilters.filter(f => !segmentDimensions.has(f.dimension))];
};

export const buildRcaPayload = (queryResult, pointData) => {
    const dimValues = pointData.custom?.dimValues || [];
    const segmentFilters = buildSegmentFilters(dimValues, queryResult.dimensions || []);
    const sidebarFilters = normalizeSidebarFilters(queryResult.filters);
    return {
        metric: {
            api_name: queryResult.colorMetricApiName || '',
            display_name: queryResult.metricLabels?.color || '',
        },
        cohort_period: queryResult.colorMetricCohortPeriod || null,
        filters: mergeRcaFilters(segmentFilters, sidebarFilters),
        absolute_change: pointData.custom?.rawColorChange,
        pct_change: pointData.custom?.rawColorChangePct,
        start_date: queryResult.firstPeriod?.start || '',
        end_date: queryResult.firstPeriod?.end || '',
        previous_period_start: queryResult.secondPeriod?.start || '',
        previous_period_end: queryResult.secondPeriod?.end || '',
    };
};
